import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { track } from "@/lib/tracking";
import { supabase } from "@/integrations/supabase/client";

/** Detect content type + id from path patterns; cheap one-shot DB lookup for /insights/:slug. */
async function detectContent(pathname: string): Promise<{ kind?: string; content_id?: string }> {
  // /insights/:slug — slug-based detail pages (covers blog/news/webinar/evidence/etc.)
  const m = pathname.match(/^\/(insights|news|webinars|blog|evidence|workflows)\/([^/?#]+)/);
  if (!m) return {};
  const slug = m[2];
  try {
    const { data } = await supabase
      .from("insights_public")
      .select("id, kind")
      .eq("slug", slug)
      .maybeSingle();
    if (data) return { kind: data.kind as string, content_id: data.id };
  } catch {
    /* ignore */
  }
  // fallback: at least record the section
  return { kind: m[1] };
}

export default function PageTracker() {
  const { pathname, search } = useLocation();
  const enteredAt = useRef<number>(Date.now());
  const lastPath = useRef<string>("");
  const flushed = useRef<boolean>(false);

  useEffect(() => {
    if (pathname.startsWith("/admin")) return;
    // TAXONOMY: `path` is pathname ONLY; raw query goes in metadata.query.
    const query = search.replace(/^\?/, "");

    // Emit pageleave for the previous page (once per page — flushed flag dedupes
    // against visibilitychange/pagehide which may have already flushed it).
    if (lastPath.current && lastPath.current !== pathname && !flushed.current) {
      const dwell_ms = Date.now() - enteredAt.current;
      if (dwell_ms >= 500) {
        track("pageleave", { dwell_ms }, lastPath.current);
      }
      flushed.current = true;
    }

    enteredAt.current = Date.now();
    lastPath.current = pathname;
    flushed.current = false;

    // Fire pageview with content metadata
    (async () => {
      const meta = await detectContent(pathname);
      const metaWithQuery = query ? { ...meta, query } : meta;
      track("pageview", metaWithQuery, pathname);
    })();
  }, [pathname, search]);

  // Flush dwell on tab hide / unload (idempotent via flushed flag).
  useEffect(() => {
    const flush = () => {
      if (!lastPath.current || flushed.current) return;
      const dwell_ms = Date.now() - enteredAt.current;
      if (dwell_ms < 500) return;
      track("pageleave", { dwell_ms }, lastPath.current);
      flushed.current = true;
      enteredAt.current = Date.now();
    };
    const onVis = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", flush);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", flush);
    };
  }, []);

  return null;
}
